import { ChangeDetectorRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UploadedFile } from '@shared/services/tab-examenes.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

export interface UploadedLocalFile {
  fileName: string;
  fileType: string;
  fileSize: number;
  fileData: string | ArrayBuffer | null;
  previewUrl: string | null;
  isUploaded: boolean;
  originalFile: File;
  destination: string | null;
}

@Component({
  selector: 'soph-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.Default
})
export class FileUploadComponent {
  readonly snackBar = inject(MatSnackBar);
  constructor(private cdr: ChangeDetectorRef) { }

  @Input() label: string = 'Cargar archivo';
  @Input() control: FormControl = new FormControl<any>({});
  @Input() error = 'Campo requerido';
  @Output() archivoSeleccionado = new EventEmitter<File>();
  @Input() hasDocumentUrl = false;
  @Input() fileName: string = '';
  @Input() url: string = '';
  @Input() isDisabled: boolean = false;

  @Output() onFileDeleted: EventEmitter<UploadedLocalFile> = new EventEmitter<UploadedLocalFile>();

  protected selectedImage: string | ArrayBuffer | null = null;
  protected showMessage: boolean = false;
  protected showEye: boolean = false;
  protected showDelete: boolean = false;
  protected esp: boolean = true;
  protected currentLanguage = 'es';
  protected hoverText: string | undefined;
  protected uploading: boolean = false;
  protected percentLoaded: number = 0;
  protected documentosCargados: WritableSignal<UploadedFile[]> = signal([]);
  protected maximoCarga: boolean = false;
  protected formatInvalid: boolean = false;

  async onFileSelected(event: Event): Promise<void> {
    event.stopPropagation();
    event.preventDefault();

    const input = event.target as HTMLInputElement;
    this.maximoCarga = false;
    this.formatInvalid = false;

    if (input.files && input.files.length > 0) {
      const filesArray: File[] = Array.prototype.slice.call(input.files);
      for (const file of filesArray) {
        const fileType = file.type;
        const fileSizeMb = file.size / (1024 * 1024);

        if (fileSizeMb > 3) {
          this.maximoCarga = true;
          continue;
        }
        if (!(fileType.startsWith('image/') || fileType === 'application/pdf')) {
          this.formatInvalid = true;
          continue;
        }

        await new Promise<void>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            let previewUrl: string | null = null;
            if (fileType.startsWith('image/')) {
              previewUrl = URL.createObjectURL(file);
            } else if (fileType === 'application/pdf') {
              previewUrl = URL.createObjectURL(file);
            }

            // Guardar en el FormControl como nuevo array (reactividad)
            const nuevoArchivo: UploadedLocalFile = {
              fileName: file.name,
              fileType: fileType,
              fileSize: file.size,
              fileData: reader.result,
              previewUrl: previewUrl,
              isUploaded: false,
              originalFile: file,
              destination: null
            };
            this.control.setValue([...this.control.value, nuevoArchivo]);
            this.cdr.detectChanges();

            this.hasDocumentUrl = true;

            resolve();
          };
          if (fileType.startsWith('image/')) {
            reader.readAsDataURL(file);
          } else if (fileType === 'application/pdf') {
            reader.readAsArrayBuffer(file);
          }
        });
      }
      input.value = '';
    }
  }

  onFileOpen(event: Event, file: any) {
    event.stopPropagation();
    event.preventDefault();

    if (file && file.previewUrl) {
      window.open(file.previewUrl, '_blank');
    }
  }

  onFileDelete(event: Event, file: UploadedLocalFile, index: number) {
    event.stopPropagation();
    event.preventDefault();

    if (!file.isUploaded) {
      if (Array.isArray(this.control.value)) {
        const updated = this.control.value.filter((_, idx) => idx !== index);
        this.control.setValue(updated);
        this.cdr.detectChanges();
        if (updated.length === 0) {
          this.hasDocumentUrl = false;
          this.showMessage = false;
          this.showDelete = false;
          this.showEye = false;
        }
      }
    }

    this.onFileDeleted.emit(file);
  }

  isControlInvalid(): boolean {
    return this.control.invalid && (this.control.dirty || this.control.touched);
  }
}
